"use client"

import { useEffect, useRef } from "react"
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"

import { TonePill } from "@/components/graphite/surface"
import { stageHex, stageTint, TINT } from "@/lib/stage-colors"
import {
  CALENDAR_VIEWS,
  DAY_START_HOUR,
  EVENT_STAGE,
  EVENT_TYPES,
  HOUR_HEIGHT,
  OPENS_AT_HOUR,
  WEEKDAYS,
  WEEKDAYS_SHORT,
  clockOf,
  dayGrid,
  endOf,
  eventType,
  eventsOn,
  hourLabel,
  hourRows,
  isDeclined,
  monthGrid,
  placeEvents,
  startOf,
  weekGrid,
  type CalendarView,
  type EventType,
  type GridDay,
} from "@/lib/calendar-grid"
import { cn } from "@/lib/utils"
import type { Event } from "openapi/api/model"

/**
 * The calendar, from canvas artboards 33b and 33c.
 *
 * The structure is lifted whole from a scheduling calendar that has seen
 * production use, where ours was a stub: one toolbar with the period label
 * left, a filterable legend, and the view switcher right; the chevrons living
 * inside the header row rather than floating above it; a month cell whose pill
 * density steps with the day's load; and a 5am–midnight grid at 48px an hour
 * that packs overlapping events into lanes.
 *
 * What did not come over is its skin. A saturated header band became a quiet
 * header row, and colours keyed to that product's own domain became the four
 * event types RecruiterOS already stores.
 *
 * Purely presentational — every event, figure and handler arrives from
 * `useCalendar`.
 */

const colourOf = (event: Event) => stageHex(EVENT_STAGE[eventType(event)])

/** One event inside a month cell. */
function MonthPill({
  event,
  density,
  email,
  onOpen,
}: {
  event: Event
  /** How many events share the cell — 33b tightens the pill as the day fills. */
  density: number
  email?: string
  onOpen: () => void
}) {
  const at = startOf(event)
  const hex = colourOf(event)
  const declined = isDeclined(event, email)
  const stacked = density === 1

  return (
    <button
      type="button"
      onClick={(clickEvent) => {
        // The cell behind this opens a composer on that day; the pill opens the
        // event it names.
        clickEvent.stopPropagation()
        onOpen()
      }}
      className={cn(
        "flex w-full min-w-0 overflow-hidden rounded text-left",
        stacked ? "flex-col gap-[3px] px-[7px] py-1.5" : "items-center gap-1.5",
        !stacked && (density >= 3 ? "px-1.5 py-[3px]" : "px-[7px] py-1"),
        declined && "opacity-55",
      )}
      style={{ backgroundColor: stageTint(hex, TINT.pill) }}
    >
      <span
        className={cn(
          "flex-none font-mono font-medium leading-[1.2]",
          stacked ? "text-[10px]" : density >= 3 ? "text-[10.5px]" : "text-[11px]",
        )}
        style={{ color: hex }}
      >
        {at ? clockOf(at) : "—"}
      </span>
      <span
        className={cn(
          "min-w-0 truncate font-medium",
          stacked
            ? "text-[11.5px] leading-[1.25]"
            : density >= 3
              ? "text-[10.5px] leading-[1.2]"
              : "text-[11px] leading-[1.2]",
        )}
        style={{ color: hex }}
      >
        {event.title ?? "Untitled event"}
      </span>
    </button>
  )
}

function MonthCell({
  day,
  events,
  email,
  onOpenEvent,
  onCreate,
  onOpenDay,
}: {
  day: GridDay
  events: Event[]
  email?: string
  onOpenEvent: (event: Event) => void
  onCreate: (day: Date, hour?: number) => void
  onOpenDay: (day: Date) => void
}) {
  /* 33b: four or more collapse to two and a count, rather than four slivers. */
  const shown = events.length >= 4 ? events.slice(0, 2) : events
  const hiddenCount = events.length - shown.length

  return (
    <div
      role="gridcell"
      tabIndex={0}
      onClick={() => onCreate(day.date)}
      onKeyDown={(keyEvent) => {
        /*
          Only the cell's own Enter. A pill and the "+N more" button sit inside
          this cell, and their Enter bubbles here — where `preventDefault` used
          to stop the button activating, so a keyboard user could not open
          anything the cell holds, only ever create a new event.
        */
        if (keyEvent.key === "Enter" && keyEvent.target === keyEvent.currentTarget) {
          keyEvent.preventDefault()
          onCreate(day.date)
        }
      }}
      className={cn(
        "flex min-h-[92px] cursor-pointer flex-col gap-1 overflow-hidden border-b border-r p-[7px] transition-colors hover:bg-foreground/[0.03]",
        !day.inMonth && "opacity-45",
      )}
    >
      <span
        className={cn(
          "self-end p-1 font-mono text-[11.5px] leading-none",
          day.isToday
            ? "rounded-full bg-primary px-1.5 py-1 font-medium text-primary-foreground"
            : "text-foreground",
        )}
      >
        {day.date.getDate()}
      </span>

      {shown.map((event, index) => (
        <MonthPill
          key={event.id ?? index}
          event={event}
          density={events.length}
          email={email}
          onOpen={() => onOpenEvent(event)}
        />
      ))}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={(clickEvent) => {
            clickEvent.stopPropagation()
            onOpenDay(day.date)
          }}
          className="self-start px-1.5 py-0.5 text-[11px] font-medium leading-[1.2] text-muted-foreground transition-colors hover:text-foreground"
        >
          +{hiddenCount} more
        </button>
      )}
    </div>
  )
}

function MonthView({
  cursor,
  events,
  email,
  onOpenEvent,
  onCreate,
  onOpenDay,
}: {
  cursor: Date
  events: Event[]
  email?: string
  onOpenEvent: (event: Event) => void
  onCreate: (day: Date, hour?: number) => void
  onOpenDay: (day: Date) => void
}) {
  const days = monthGrid(cursor)

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-auto rounded-[10px] border bg-card">
      <div className="grid min-w-[700px] flex-none grid-cols-7 border-b">
        {WEEKDAYS_SHORT.map((name) => (
          <div
            key={name}
            className="px-[7px] py-2.5 text-center text-[11px] font-medium text-muted-foreground"
          >
            {name}
          </div>
        ))}
      </div>
      {/* The grid keeps a legible width and scrolls under a narrow pane; seven
          columns squeezed into 390px is seven unreadable slivers. */}
      <div
        role="grid"
        className="grid min-w-[700px] flex-1 grid-cols-7"
        style={{ gridTemplateRows: `repeat(${days.length / 7}, minmax(0, 1fr))` }}
      >
        {days.map((day) => (
          <MonthCell
            key={day.date.toISOString()}
            day={day}
            events={eventsOn(events, day.date)}
            email={email}
            onOpenEvent={onOpenEvent}
            onCreate={onCreate}
            onOpenDay={onOpenDay}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * How much an event block can say, given the room it has.
 *
 * A 20-minute screening is a coloured sliver with a name on it; a two-hour
 * panel has space for its clock and for where it is happening. Tiering blocks
 * by pixel height matters most in the day view, where one column is the full
 * width and a tall block still whispering at 11px reads as broken rather than
 * quiet.
 */
function blockText(pixels: number): {
  title: string
  showTime: boolean
  showWhere: boolean
} {
  if (pixels >= 96) return { title: "text-[13px]", showTime: true, showWhere: true }
  if (pixels >= 64) return { title: "text-[12px]", showTime: true, showWhere: true }
  if (pixels >= 40) return { title: "text-[11px]", showTime: true, showWhere: false }
  return { title: "text-[11px]", showTime: false, showWhere: false }
}

/**
 * The time grid, which draws a week and a day alike.
 *
 * A day is a week of one column: the hours, the lane packing and the event
 * blocks are all the same, so 33c's grid answers both and the switcher's third
 * option is not a second design. What the single column does change is how much
 * each thing can say — the heading writes its weekday out in full, and the
 * blocks, now the width of the card, take the larger tiers of `blockText`.
 */
function TimeGrid({
  days,
  events,
  email,
  onOpenEvent,
  onCreate,
}: {
  days: GridDay[]
  events: Event[]
  email?: string
  onOpenEvent: (event: Event) => void
  onCreate: (day: Date, hour?: number) => void
}) {
  const scroller = useRef<HTMLDivElement>(null)
  const hours = hourRows()
  /** One column is the day view; seven is the week. */
  const single = days.length === 1
  const period = `${days[0]?.date.toDateString() ?? ""}|${days.length}`

  /*
    33c: "opens scrolled to 8am". Nobody's day starts at five.

    Keyed on the period rather than on mount: stepping to the next day and
    switching week → day both put you somewhere new, and arriving there still
    scrolled to wherever you had dragged the last one is how you miss a
    morning.
  */
  useEffect(() => {
    if (scroller.current) {
      scroller.current.scrollTop = (OPENS_AT_HOUR - DAY_START_HOUR) * HOUR_HEIGHT
    }
  }, [period])

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border bg-card">
      <div
        className="flex flex-none border-b"
        style={{ paddingRight: "var(--time-grid-gutter, 0px)" }}
      >
        <div className="w-14 flex-none" />
        {days.map((day) => (
          <div
            key={day.date.toISOString()}
            className={cn(
              "flex-1 px-2 py-2.5 text-center",
              // One column has the room to name its weekday in full and set it
              // beside the date rather than above it.
              single && "flex items-center justify-center gap-2",
            )}
          >
            <div className="text-[11px] font-medium text-muted-foreground">
              {single ? WEEKDAYS[day.date.getDay()] : WEEKDAYS_SHORT[day.date.getDay()]}
            </div>
            <div
              className={cn(
                "inline-flex size-6 items-center justify-center font-mono text-[12.5px] leading-none",
                !single && "mt-1",
                day.isToday && "rounded-full bg-primary font-medium text-primary-foreground",
              )}
            >
              {day.date.getDate()}
            </div>
          </div>
        ))}
      </div>

      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        <div className="flex" style={{ height: hours.length * HOUR_HEIGHT }}>
          <div className="w-14 flex-none">
            {hours.map((hour) => (
              <div
                key={hour}
                className="relative pr-2 text-right"
                style={{ height: HOUR_HEIGHT }}
              >
                <span className="absolute right-2 -top-[6px] font-mono text-[10.5px] text-muted-foreground">
                  {hourLabel(hour)}
                </span>
              </div>
            ))}
          </div>

          {days.map((day) => {
            // Every event that touches this day, not only those that start on it.
            const placed = placeEvents(events, day.date)
            return (
              <div key={day.date.toISOString()} className="relative flex-1 border-l">
                {hours.map((hour) => (
                  /*
                    A real button, not a div that happens to listen for clicks:
                    an hour is a thing you can do, and the only other way to
                    reach it — "New Event" in the header — opens at the default
                    time rather than this one. The month's cells are reachable
                    the same way.
                  */
                  <button
                    key={hour}
                    type="button"
                    aria-label={`New event at ${hourLabel(hour)}, ${
                      WEEKDAYS[day.date.getDay()]
                    } ${day.date.getDate()}`}
                    onClick={() => {
                      const at = new Date(day.date)
                      at.setHours(hour, 0, 0, 0)
                      // The hour as well as the day: the composer cannot read
                      // one off a Date it is only given as a calendar date.
                      onCreate(at, hour)
                    }}
                    className="block w-full cursor-pointer border-b transition-colors hover:bg-foreground/[0.03]"
                    style={{ height: HOUR_HEIGHT }}
                  />
                ))}

                {placed.map(({ event, top, height, lane, lanes, continuesBefore, continuesAfter }, index) => {
                  const hex = colourOf(event)
                  const at = startOf(event)
                  const until = endOf(event)
                  const declined = isDeclined(event, email)
                  const name = event.title ?? "Untitled event"
                  /*
                    What this block is, not what the whole event is. A block
                    continuing from yesterday is drawn from the 5am top, and
                    labelling it with the event's own 22:00 start read as a
                    meeting that began at five. An arrow says where it carries
                    on; the title attribute keeps the full span.
                  */
                  const span = at ? `${clockOf(at)}${until ? ` – ${clockOf(until)}` : ""}` : ""
                  const when = !at
                    ? ""
                    : continuesBefore && continuesAfter
                      ? "all day →"
                      : continuesBefore
                        ? `→ ${until ? clockOf(until) : ""}`.trim()
                        : continuesAfter
                          ? `${clockOf(at)} →`
                          : span
                  const where = event.location?.trim()
                  // `placeEvents` already applied the minimum, and packed lanes by it.
                  const pixels = (height / 60) * HOUR_HEIGHT
                  const text = blockText(pixels)
                  return (
                    <button
                      key={event.id ?? index}
                      type="button"
                      onClick={() => onOpenEvent(event)}
                      /* The tiers below drop the clock and the place as a block
                         gets shorter, so the full reading lives on the title
                         attribute for the ones that had to go quiet. */
                      title={[span, name, where].filter(Boolean).join(" · ")}
                      className={cn(
                        "absolute overflow-hidden rounded px-1.5 py-1 text-left",
                        declined && "opacity-55",
                      )}
                      style={{
                        top: (top / 60) * HOUR_HEIGHT,
                        height: pixels,
                        left: `calc(${(lane / lanes) * 100}% + 2px)`,
                        width: `calc(${100 / lanes}% - 4px)`,
                        backgroundColor: stageTint(hex, TINT.pill),
                        borderLeft: `2px solid ${hex}`,
                      }}
                    >
                      <span
                        className={cn(
                          "block truncate font-medium leading-[1.25]",
                          text.title,
                        )}
                        style={{ color: hex }}
                      >
                        {name}
                      </span>
                      {text.showTime && when && (
                        <span className="block truncate font-mono text-[10px] leading-[1.3] text-muted-foreground">
                          {when}
                        </span>
                      )}
                      {text.showWhere && where && (
                        <span className="block truncate text-[10.5px] leading-[1.3] text-muted-foreground">
                          {where}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function GraphiteCalendar({
  view,
  cursor,
  label,
  onStep,
  onToday,
  events,
  hidden,
  onToggleType,
  email,
  isLoading,
  isError,
  truncated,
  onOpenEvent,
  onCreate,
  onOpenDay,
}: {
  view: CalendarView
  cursor: Date
  label: string
  onStep: (direction: 1 | -1) => void
  onToday: () => void
  events: Event[]
  hidden: EventType[]
  onToggleType: (type: EventType) => void
  email?: string
  isLoading: boolean
  isError: boolean
  truncated?: number
  onOpenEvent: (event: Event) => void
  onCreate: (day: Date, hour?: number) => void
  onOpenDay: (day: Date) => void
}) {
  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-3.5 px-4 pb-5 pt-4 @2xl:px-[28px]">
      {/*
        The legend, which is also the filter.

        On a surface of its own rather than floating on the page ground: in
        light mode the page behind it is grey, and a bare row of words on grey
        reads as an unfinished area rather than as a control. It is the same
        card the grid below it sits on.

        Each type is a chip in its own colour — the tint and the hue of the
        event blocks it stands for, so the legend looks like the thing it
        labels. Hiding a type drains the chip rather than only striking the
        word, which is the state you can read at a glance from across the row.
      */}
      <div className="flex flex-none flex-wrap items-center gap-x-4 gap-y-2 rounded-[10px] border bg-card px-3 py-2.5">
        {EVENT_TYPES.map((type) => {
          const off = hidden.includes(type)
          return (
            <button
              key={type}
              type="button"
              aria-pressed={!off}
              onClick={() => onToggleType(type)}
              className="rounded-full transition-opacity hover:opacity-80"
            >
              <TonePill hex={stageHex(EVENT_STAGE[type])} muted={off}>
                <span className={cn(off && "line-through")}>{type}</span>
              </TonePill>
            </button>
          )
        })}
        <span className="text-[11px] leading-none text-muted-foreground">
          Click a type to hide it
        </span>

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous"
            onClick={() => onStep(-1)}
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={onToday}
            className="rounded-md px-2 py-1 text-[11.5px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Today
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={() => onStep(1)}
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      {truncated !== undefined && (
        /*
          `GET /events` has no date range, so the calendar reads one page and
          filters it. When the server says it holds more than arrived, the page
          says so — a month that quietly drops the events past the page boundary
          is a month you would plan against.
        */
        <p className="flex-none text-[11.5px] text-muted-foreground">
          Showing {events.length} of {truncated} events — this view cannot ask the
          server for a date range yet.
        </p>
      )}

      {isLoading || isError ? (
        <div className="flex min-h-0 flex-1 items-start rounded-[10px] border bg-card p-4">
          {isError ? (
            <p className="text-[12.5px] text-muted-foreground">
              The calendar cannot be read right now.
            </p>
          ) : (
            <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              Reading the calendar…
            </p>
          )}
        </div>
      ) : view === "month" ? (
        <MonthView
          cursor={cursor}
          events={events}
          email={email}
          onOpenEvent={onOpenEvent}
          onCreate={onCreate}
          onOpenDay={onOpenDay}
        />
      ) : (
        <TimeGrid
          days={view === "week" ? weekGrid(cursor) : dayGrid(cursor)}
          events={events}
          email={email}
          onOpenEvent={onOpenEvent}
          onCreate={onCreate}
        />
      )}

      <span className="sr-only" aria-live="polite">
        {label}
      </span>
    </div>
  )
}

/** The Month / Week / Day switcher, which lives in the page header. */
export function CalendarViewSwitch({
  view,
  onChange,
}: {
  view: CalendarView
  onChange: (next: CalendarView) => void
}) {
  return (
    <div className="inline-flex flex-none items-center gap-0.5 rounded-full border p-0.5">
      {CALENDAR_VIEWS.map((entry) => (
        <button
          key={entry}
          type="button"
          onClick={() => onChange(entry)}
          aria-pressed={view === entry}
          className={cn(
            "rounded-full px-[13px] py-1.5 text-[11.5px] font-medium capitalize transition-colors",
            view === entry
              ? "bg-foreground/[0.08] text-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {entry}
        </button>
      ))}
    </div>
  )
}
